import React, { useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import {
  Video, MapPin, Phone, ClipboardCheck, CalendarClock, Ban, UserX, ExternalLink,
  Pencil, Trash2, Loader2, History, User, RefreshCw,
} from 'lucide-react';
import { CommActions } from '../CommActions';
import { ConflictWarning } from './ConflictWarning';
import { TranscriptCard } from './TranscriptCard';
import { openSummaryPanel, emitLeadChanged } from '../../lib/events';
import {
  MEETING_STATUS_COLORS, OUTCOME_COLORS, fmtMeetingDate, fmtMeetingRange,
  conflictDetail, errMessage,
} from '../../lib/meetings';
import { fmtDateTime, inr } from '../../lib/format';
import logger from '../../lib/logger';

/** Company + client phone subtitle, computed outside JSX. */
const clientMeta = (m) => [m.company_name, m.client_phone].filter(Boolean).join(' · ');

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <span className="text-xs text-muted-foreground shrink-0 w-28">{label}</span>
    <span className="text-xs text-right flex-1 min-w-0">{children}</span>
  </div>
);

const MODE_ICON = { 'In-Person': MapPin, 'Video Call': Video, 'Phone Call': Phone, 'WhatsApp Call': Phone };

const OutcomeCard = ({ o }) => (
  <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5" data-testid="panel-outcome-card">
    <div className="flex items-center justify-between">
      <Badge variant="outline" className={`text-[10px] ${OUTCOME_COLORS[o.outcome] || ''}`}>{o.outcome}</Badge>
      <span className="text-[10px] text-muted-foreground">by {o.recorded_by_name} · {fmtDateTime(o.recorded_at)}</span>
    </div>
    {o.summary && <div className="text-xs">{o.summary}</div>}
    {o.key_points && <div className="text-[11px] text-muted-foreground"><b>Key points:</b> {o.key_points}</div>}
    {(o.objections || []).length > 0 && (
      <div className="text-[11px] text-muted-foreground"><b>Objections:</b> {o.objections.join(', ')}{o.objection_details ? ` — ${o.objection_details}` : ''}</div>
    )}
    {o.next_action && <div className="text-[11px]"><b>Next:</b> {o.next_action}{o.next_action_date ? ` by ${o.next_action_date}` : ''}</div>}
    {o.deal_value_discussed ? <div className="text-[11px]"><b>Deal value discussed:</b> {inr(o.deal_value_discussed)}</div> : null}
    {o.last_edited_at && <div className="text-[10px] text-orange-600">Edited {fmtDateTime(o.last_edited_at)} by {o.last_edited_by_name}{o.edit_reason ? ` — ${o.edit_reason}` : ''}</div>}
  </div>
);

const RescheduleForm = ({ meeting, canOverride, onDone, onCancel }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [by, setBy] = useState('us');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  const submit = async (withOverride = false) => {
    if (!start || !end || !reason.trim()) { toast.error('New times and a reason are required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/reschedule`, {
        start_at: start, end_at: end, by, reason: reason.trim(),
        override_conflict: withOverride, override_reason: withOverride ? overrideReason.trim() : null,
      });
      toast.success('Meeting rescheduled — history kept');
      onDone(data);
    } catch (e) {
      const c = conflictDetail(e);
      if (c) setConflict(c);
      else toast.error(errMessage(e, 'Reschedule failed'));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="reschedule-form">
      <div className="text-xs font-semibold flex items-center gap-1.5"><CalendarClock size={13} /> Reschedule Meeting</div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1"><Label className="text-[11px]">New Start (IST)</Label>
          <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 text-xs" data-testid="reschedule-start-input" /></div>
        <div className="space-y-1"><Label className="text-[11px]">New End (IST)</Label>
          <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 text-xs" data-testid="reschedule-end-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1"><Label className="text-[11px]">Rescheduled By</Label>
          <Select value={by} onValueChange={setBy}>
            <SelectTrigger className="h-8 text-xs" data-testid="reschedule-by-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="us">Us (our team)</SelectItem>
              <SelectItem value="client">Client</SelectItem>
            </SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-[11px]">Reason (required)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-8 text-xs" data-testid="reschedule-reason-input" /></div>
      </div>
      {conflict && (
        <ConflictWarning conflict={conflict} canOverride={canOverride} overrideReason={overrideReason}
          setOverrideReason={setOverrideReason} onOverride={() => submit(true)} saving={saving} />
      )}
      <div className="flex gap-2">
        <Button size="sm" className="h-8 flex-1" onClick={() => submit(false)} disabled={saving} data-testid="reschedule-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Confirm Reschedule
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={onCancel} data-testid="reschedule-cancel-btn">Back</Button>
      </div>
    </div>
  );
};

const CancelForm = ({ meeting, onDone, onCancel }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error('A cancellation reason is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/cancel`, { reason: reason.trim() });
      toast.success('Meeting cancelled');
      onDone(data);
    } catch (e) { toast.error(errMessage(e, 'Cancel failed')); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-red-200 p-3 space-y-2" data-testid="cancel-form">
      <div className="text-xs font-semibold text-red-700 flex items-center gap-1.5"><Ban size={13} /> Cancel Meeting</div>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Cancellation reason (required)"
        className="h-8 text-xs" data-testid="cancel-reason-input" />
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" className="h-8 flex-1" onClick={submit} disabled={saving} data-testid="cancel-confirm-btn">
          {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Confirm Cancellation
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={onCancel} data-testid="cancel-back-btn">Back</Button>
      </div>
    </div>
  );
};

const RescheduleHistory = ({ history }) => (
  <div className="space-y-1">
    <div className="text-xs font-semibold flex items-center gap-1.5"><History size={12} /> Time-slot History</div>
    {history.map((h) => (
      <div key={h.at} className="text-[11px] text-muted-foreground pl-4">
        {fmtMeetingDate(h.old_start)} → {fmtMeetingDate(h.new_start)} · by {h.by_name} ({h.rescheduled_by}) — “{h.reason}”
      </div>
    ))}
  </div>
);

const PanelActions = ({ m, setMode, onOutcome, onEdit, markNoShow, canDelete, del }) => {
  const active = m.status === 'Scheduled' || m.status === 'Rescheduled';
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {m.meeting_link && (
        <Button asChild size="sm" className="h-8 col-span-2" data-testid="panel-join-btn">
          <a href={m.meeting_link} target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1.5" /> Join Meeting</a>
        </Button>
      )}
      {m.status !== 'Cancelled' && (
        <Button size="sm" variant={m.outcome ? 'outline' : 'default'} className="h-8" onClick={onOutcome} data-testid="panel-outcome-btn">
          <ClipboardCheck size={13} className="mr-1.5" /> {m.outcome ? 'Edit Outcome' : 'Add Outcome'}
        </Button>
      )}
      {active && (
        <>
          <Button size="sm" variant="outline" className="h-8" onClick={() => setMode('reschedule')} data-testid="panel-reschedule-btn">
            <CalendarClock size={13} className="mr-1.5" /> Reschedule
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={markNoShow} data-testid="panel-noshow-btn">
            <UserX size={13} className="mr-1.5" /> No-Show
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setMode('cancel')} data-testid="panel-cancel-btn">
            <Ban size={13} className="mr-1.5" /> Cancel
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={onEdit} data-testid="panel-edit-btn">
            <Pencil size={13} className="mr-1.5" /> Edit Details
          </Button>
        </>
      )}
      <Button size="sm" variant="outline" className="h-8" onClick={() => openSummaryPanel('lead', m.lead_id)} data-testid="panel-open-lead-btn">
        <User size={13} className="mr-1.5" /> Open Lead
      </Button>
      {canDelete && (
        <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={del} data-testid="panel-delete-btn">
          <Trash2 size={13} className="mr-1.5" /> Archive
        </Button>
      )}
    </div>
  );
};

/** Side panel summary with 1-click quick actions — opened from list & calendar. */
/** Stable lead stub for CommActions — inline object would re-render it every time. */
const useLeadStub = (m) => useMemo(() => ({
  id: m?.lead_id, phone: m?.client_phone, lead_name: m?.lead_name, company_name: m?.company_name,
}), [m?.lead_id, m?.client_phone, m?.lead_name, m?.company_name]);

/** Meet link health for Video Calls: failure reason + on-demand Retry (never duplicates events). */
const MeetLinkCard = ({ m, onChanged }) => {
  const [retrying, setRetrying] = useState(false);
  if (m.mode !== 'Video Call') return null;
  const hasLink = (m.meeting_link || '').startsWith('https://meet.google.com/');
  const failed = m.meet_link_status === 'Failed — Retry Needed' || (!hasLink && !m.meeting_link);
  if (!failed) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/meetings/${m.id}/retry-meet-link`);
      if (data.ok) toast.success(`Meet link ${data.action === 'patched' ? 'repaired on the existing event' : 'generated'}`);
      else toast.error(data.error || 'Meet link retry failed');
      if (data.meeting) onChanged?.(data.meeting);
    } catch (e) { toast.error(errMessage(e, 'Meet link retry failed')); }
    finally { setRetrying(false); }
  };

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3 space-y-1.5" data-testid="meet-link-failed-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-orange-800 inline-flex items-center gap-1.5">
          <Video size={13} /> Meet Link: Failed — Retry Needed
        </span>
        <Button size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-800 hover:bg-orange-100"
          onClick={retry} disabled={retrying} data-testid="retry-meet-link-btn">
          {retrying ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />} Retry Meet Link
        </Button>
      </div>
      {m.meet_link_error && <div className="text-[11px] text-orange-700" data-testid="meet-link-error-text">{m.meet_link_error}</div>}
    </div>
  );
};

export const MeetingSidePanel = ({ meeting, open, onClose, onChanged, onOutcome, onEdit, canDelete, canOverride }) => {
  const [mode, setMode] = useState(null); // reschedule | cancel
  const m = meeting;
  const leadStub = useLeadStub(m);
  if (!m) return null;
  const ModeIcon = MODE_ICON[m.mode] || Video;

  const done = (data) => { setMode(null); onChanged?.(data); emitLeadChanged(); };

  const markNoShow = async () => {
    try {
      const { data } = await api.post(`/meetings/${m.id}/no-show`);
      toast.success('Marked as No-Show — lead Meeting Status updated');
      done(data);
    } catch (e) { toast.error(errMessage(e, 'Failed')); }
  };

  const del = async () => {
    if (!window.confirm('Move this meeting to the 30-day Archive? The linked lead is never affected.')) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success('Meeting archived — restorable for 30 days');
      onChanged?.(null);
      onClose();
    } catch (e) { toast.error(errMessage(e, 'Archive failed')); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) { setMode(null); onClose(); } }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="meeting-side-panel">
        <SheetHeader className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[10px] ${MEETING_STATUS_COLORS[m.status] || ''}`} data-testid="panel-status-badge">{m.status}</Badge>
            {m.outcome_pending && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-300" data-testid="panel-outcome-pending-badge">Outcome Pending</Badge>}
            {m.conflict_override && <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">Conflict Overridden</Badge>}
          </div>
          <SheetTitle className="font-heading text-base leading-snug" data-testid="panel-title">{m.title}</SheetTitle>
          <div className="text-xs text-muted-foreground">{fmtMeetingDate(m.start_at)} · {fmtMeetingRange(m)}</div>
        </SheetHeader>

        <div className="space-y-3 mt-2">
          <PanelActions m={m} setMode={setMode} onOutcome={onOutcome} onEdit={onEdit} markNoShow={markNoShow} canDelete={canDelete} del={del} />

          <MeetLinkCard m={m} onChanged={onChanged} />

          {mode === 'reschedule' && <RescheduleForm meeting={m} canOverride={canOverride} onDone={done} onCancel={() => setMode(null)} />}
          {mode === 'cancel' && <CancelForm meeting={m} onDone={done} onCancel={() => setMode(null)} />}

          <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{m.lead_name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{clientMeta(m)}</div>
            </div>
            <CommActions lead={leadStub} onLogged={() => emitLeadChanged()} />
          </div>

          {m.outcome && <OutcomeCard o={m.outcome} />}

          <TranscriptCard meeting={m} onChanged={async () => {
            try {
              const { data } = await api.get(`/meetings/${m.id}`);
              onChanged?.(data);
            } catch (e) {
              logger.warn('Meeting panel refresh failed (best-effort)', e);
            }
          }} />

          <Separator />
          <div>
            <Row label="Type">{m.meeting_type}</Row>
            <Row label="Mode"><span className="inline-flex items-center gap-1"><ModeIcon size={12} /> {m.mode}</span></Row>
            {m.location && <Row label="Location">{m.location}</Row>}
            <Row label="Owner">{m.owner_name || '—'}</Row>
            <Row label="Internal Team">{(m.internal_attendee_names || []).join(', ') || '—'}</Row>
            <Row label="Client Attendees">{(m.client_attendees || []).map((a) => a.name).join(', ') || '—'}</Row>
            {m.agenda && <Row label="Agenda">{m.agenda}</Row>}
            {m.pre_notes && <Row label="Pre-Notes">{m.pre_notes}</Row>}
            {m.internal_remarks && <Row label="Internal Remarks"><span className="text-orange-700">{m.internal_remarks}</span></Row>}
            {m.cancel_reason && <Row label="Cancel Reason"><span className="text-red-600">{m.cancel_reason}</span></Row>}
            <Row label="Created By">{m.created_by_name} · {fmtDateTime(m.created_at)}</Row>
          </div>

          {(m.reschedule_history || []).length > 0 && <RescheduleHistory history={m.reschedule_history} />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
